import { useState } from 'react';
import { omit } from 'ramda';
import { useBoolean } from 'shared/web';
import { PresetApiStateEnum } from '../constants/PresetApiStateEnum';
type UsePresetApiStateProps = {
  updatePreset: (presetId: string) => Promise<void> | void;
  duplicatePreset: (presetId: string) => Promise<void>;
  deletePreset: (presetId: string) => Promise<void>;
  updatePresetPosition: (from: number, to: number) => Promise<void>;
};
type UsePresetApiStateResult = {
  apiState: Record<string, PresetApiStateEnum>;
  isPresetChangeDisabled: boolean;
  disabledPresetChange: () => void;
  allowPresetChange: () => void;
  updatePresetProxy: (presetId: string) => void;
  duplicatePresetProxy: (presetId: string) => void;
  deletePresetProxy: (presetId: string) => void;
  updatePresetPositionProxy: (presetId: string, from: number, to: number) => void;
};

/**
 * Manages per-preset API loading/success/error state for CRUD operations.
 *
 * Extracted from PresetPanel to keep the component lean and allow the state
 * machine to be tested independently.
 */
export function usePresetApiState(props: UsePresetApiStateProps): UsePresetApiStateResult {
  const [apiState, setApiState] = useState<Record<string, PresetApiStateEnum>>({});
  const [isPresetChangeDisabled, disabledPresetChange, allowPresetChange] = useBoolean();
  const updatePresetProxy = (presetId: string) => {
    disabledPresetChange();
    setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.loading
    }));
    const updateResult = props.updatePreset(presetId);
    const resolvedPromise = updateResult != null && typeof updateResult === 'object' ? updateResult as Promise<void> : null;
    if (resolvedPromise == null) {
      return setTimeout(() => setApiState(prev => ({
        ...prev,
        [presetId]: PresetApiStateEnum.idle
      })), 1200);
    }
    resolvedPromise.then(() => setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.success
    }))).catch(() => setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.error
    }))).finally(() => {
      setTimeout(() => setApiState(prev => ({
        ...prev,
        [presetId]: PresetApiStateEnum.idle
      })), 1200);
      allowPresetChange();
    });
  };
  const duplicatePresetProxy = (presetId: string) => {
    disabledPresetChange();
    setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.loading
    }));
    props.duplicatePreset(presetId).then(() => setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.success
    }))).catch(() => setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.error
    }))).finally(() => {
      setTimeout(() => setApiState(prev => ({
        ...prev,
        [presetId]: PresetApiStateEnum.idle
      })), 1200);
      allowPresetChange();
    });
  };
  const deletePresetProxy = (presetId: string) => {
    setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.loading
    }));
    props.deletePreset(presetId).then(() => setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.success
    }))).catch(() => setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.error
    }))).finally(() => setTimeout(() => setApiState(prev => omit([presetId], prev)), 1200));
  };
  const updatePresetPositionProxy = (presetId: string, from: number, to: number) => {
    disabledPresetChange();
    setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.loading
    }));
    props.updatePresetPosition(from, to).then(() => setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.success
    }))).catch(() => setApiState(prev => ({
      ...prev,
      [presetId]: PresetApiStateEnum.error
    }))).finally(() => {
      setTimeout(() => setApiState(prev => ({
        ...prev,
        [presetId]: PresetApiStateEnum.idle
      })), 1200);
      allowPresetChange();
    });
  };
  return {
    apiState,
    isPresetChangeDisabled,
    disabledPresetChange,
    allowPresetChange,
    updatePresetProxy,
    duplicatePresetProxy,
    deletePresetProxy,
    updatePresetPositionProxy
  };
}